// 运营区匹配工具：从 dbus 内联实现抽出，供 ticket 扫码补票复用
// dbus 仍用内联实现，后续可迁移到此。逻辑对齐小程序 utils/region.js

// 解析 regionList 接口返回的原始数组，把 boundaryPointsJson 字符串转成坐标数组
export function parseRegionList(rawRegionList) {
  if (!rawRegionList || !rawRegionList.length) return []
  return rawRegionList.map(region => {
    // boundaryPointsJson: [[经度, 纬度], ...]
    const pointsArray = JSON.parse(region.boundaryPointsJson || '[]')
    const coordinates = pointsArray.map(point => ({
      longitude: point[0],
      latitude: point[1]
    }))
    // 确保多边形闭合
    if (coordinates.length > 1) {
      const first = coordinates[0]
      const last = coordinates[coordinates.length - 1]
      if (first.longitude !== last.longitude || first.latitude !== last.latitude) {
        coordinates.push({ ...first })
      }
    }
    return {
      id: region.id,
      regionId: region.regionId,
      name: region.regionName,
      coordinates: coordinates,
      operateStartTime: region.operateStartTime,
      operateEndTime: region.operateEndTime,
      customerServicePhone: region.customerServicePhone,
      customerServiceTime: region.customerServiceTime,
      maxPassengers: region.maxPassengers,
      allowOnboardTicket: region.allowOnboardTicket,
      privateCar: region.privateCar,
      status: region.status
    }
  })
}

// 射线法判断点是否在多边形内
export function isPointInPolygon(point, polygon) {
  const x = point.longitude
  const y = point.latitude
  let inside = false
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].longitude
    const yi = polygon[i].latitude
    const xj = polygon[j].longitude
    const yj = polygon[j].latitude
    const intersect =
      ((yi > y) !== (yj > y)) &&
      (x < ((xj - xi) * (y - yi)) / (yj - yi) + xi)
    if (intersect) inside = !inside
  }
  return inside
}

// 在 regionList 中找定位点所在的运营区，返回匹配项；无匹配返回 null
export function findRegionByLocation(regionList, lat, lng) {
  if (!regionList || !regionList.length) return null
  const point = { latitude: lat, longitude: lng }
  for (let i = 0; i < regionList.length; i++) {
    const region = regionList[i]
    if (region.coordinates && region.coordinates.length && isPointInPolygon(point, region.coordinates)) {
      return region
    }
  }
  return null
}

// WGS84 转 GCJ02（火星坐标系）
// H5 navigator.geolocation 返回 WGS84，而运营区多边形 boundaryPointsJson 与高德均为 GCJ02，
// 不转换会有百米级偏移，导致 findRegionByLocation 匹配不上、regionId 取不到。
// 对齐小程序 wx.getLocation({ type: 'gcj02' })。国境外近似无偏移，原样返回。
const PI = Math.PI
const GCJ_A = 6378245.0
const GCJ_EE = 0.00669342162296594323

function transformLat(lng, lat) {
  let ret = -100.0 + 2.0 * lng + 3.0 * lat + 0.2 * lat * lat + 0.1 * lng * lat + 0.2 * Math.sqrt(Math.abs(lng))
  ret += (20.0 * Math.sin(6.0 * lng * PI) + 20.0 * Math.sin(2.0 * lng * PI)) * 2.0 / 3.0
  ret += (20.0 * Math.sin(lat * PI) + 40.0 * Math.sin(lat / 3.0 * PI)) * 2.0 / 3.0
  ret += (160.0 * Math.sin(lat / 12.0 * PI) + 320 * Math.sin(lat * PI / 30.0)) * 2.0 / 3.0
  return ret
}

function transformLng(lng, lat) {
  let ret = 300.0 + lng + 2.0 * lat + 0.1 * lng * lng + 0.1 * lng * lat + 0.1 * Math.sqrt(Math.abs(lng))
  ret += (20.0 * Math.sin(6.0 * lng * PI) + 20.0 * Math.sin(2.0 * lng * PI)) * 2.0 / 3.0
  ret += (20.0 * Math.sin(lng * PI) + 40.0 * Math.sin(lng / 3.0 * PI)) * 2.0 / 3.0
  ret += (150.0 * Math.sin(lng / 12.0 * PI) + 300.0 * Math.sin(lng / 30.0 * PI)) * 2.0 / 3.0
  return ret
}

export function wgs84ToGcj02(lng, lat) {
  // 国境外近似无偏移
  if (lng < 72.004 || lng > 137.8347 || lat < 0.8293 || lat > 55.8271) {
    return { lng, lat }
  }
  let dLat = transformLat(lng - 105.0, lat - 35.0)
  let dLng = transformLng(lng - 105.0, lat - 35.0)
  const radLat = lat / 180.0 * PI
  let magic = Math.sin(radLat)
  magic = 1 - GCJ_EE * magic * magic
  const sqrtMagic = Math.sqrt(magic)
  dLat = (dLat * 180.0) / ((GCJ_A * (1 - GCJ_EE)) / (magic * sqrtMagic) * PI)
  dLng = (dLng * 180.0) / (GCJ_A / sqrtMagic * Math.cos(radLat) * PI)
  return { lng: lng + dLng, lat: lat + dLat }
}
